import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  CompanyLifeState,
  CompanyStateTimelineCompositeEntity,
  CompanyStateTimelineEntity,
  COUNTRY_FEATURES,
} from '@nk/data-entities-lib';
import { DictList } from '@nk/utils-lib';
import { concatMap, forkJoin, from, map, mergeMap, Observable, of } from 'rxjs';
import { companyLifeStateFromTimeline } from '../utils/company-state.utils';

@Injectable({
  providedIn: 'root',
})
export class CompanyLifeStateService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
  ) {}

  public companyStateTimelineCompositeByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyStateTimelineCompositeByCompany,
  );
  public fetchCompanyStateTimelineCompositeByCompany(
    companyUuid: string,
    fromDate?: Date | null,
    toDate?: Date | null,
    companyStateCategoryKeys?: string[],
    currentFlag?: boolean | null,
  ): Observable<CompanyStateTimelineCompositeEntity[]> {
    let httpParams = new HttpParams();
    if (fromDate) {
      httpParams = httpParams.append('active_period_from_date', String(fromDate));
    }
    if (toDate) {
      httpParams = httpParams.append('active_period_to_date', String(toDate));
    }
    if (currentFlag) {
      httpParams = httpParams.append('current_flag', String(currentFlag));
    }
    if (companyStateCategoryKeys) {
      httpParams = httpParams.append('company_state_category_keys', String(companyStateCategoryKeys));
    }
    return this._analysisApiAccess.get<CompanyStateTimelineCompositeEntity[]>(
      `company/${companyUuid}/company-state-timeline-composite`,
      { params: httpParams },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public getCompanyLifeState(companyUuid: string): Observable<CompanyLifeState> {
    return this.companyStateTimelineCompositeByCompanyCache
      .get(
        companyUuid,
        null,
        null,
        [
          'company_life',
          'under_reconstruction',
          'registered_bankrupt',
          'registered_liquidation',
          'registered_forced_liquidation',
          'confirmed_dead',
        ],
        true,
      )
      .pipe(
        map((states) => {
          return companyLifeStateFromTimeline(states);
        }),
      );
  }

  public streamCompanyStateFromCompanyStateCategoryKeys(
    companyUuidList: string[],
    companyStateCategoryKeys: string[],
  ): Observable<DictList<CompanyStateTimelineCompositeEntity>> {
    return from(companyUuidList).pipe(
      concatMap((companyUuid) => {
        return forkJoin([
          this.companyStateTimelineCompositeByCompanyCache.get(companyUuid, null, null, companyStateCategoryKeys),
          of(companyUuid),
        ]);
      }),
      mergeMap((payload) => {
        const companyStateTimelineComposite = payload[0];
        const companyUuid = payload[1];
        return of({
          [companyUuid]: companyStateTimelineComposite,
        });
      }),
    );
  }

  public companyStateTimelineByCompanyStateCategoryAndCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyStateTimelineByCompanyStateCategoryAndCompany,
  );
  public fetchCompanyStateTimelineByCompanyStateCategoryAndCompany(
    companyUuid: string,
    companyStateCategoryUuid: string,
    fromDate: Date,
    toDate: Date,
  ): Observable<CompanyStateTimelineEntity[]> {
    let httpParams = new HttpParams();
    if (fromDate) {
      httpParams = httpParams.append('active_period_from_date', String(fromDate));
    }
    if (toDate) {
      httpParams = httpParams.append('active_period_to_date', String(toDate));
    }

    return this._analysisApiAccess.get<CompanyStateTimelineEntity[]>(
      `company/${companyUuid}/company-state-category/${companyStateCategoryUuid}/company-state-timeline`,
      { params: httpParams },
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }
}
